import { useState } from "react";
import { Controller, FormProvider, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { IconButton } from "@mui/material";
import KeyOutlinedIcon from "@mui/icons-material/KeyOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import InputField from "@components/common/InputField";
import Button from "@components/common/Button/Button";
import PasswordStrengthHints from "@components/common/AuthPasswordFields/PasswordStrengthHints";
import { AUTH_INPUT_FIELD_PROPS, AUTH_SUBMIT_BUTTON_CLASS } from "@components/common/AuthLayout";
import useChangePasswordMutate from "../services/queries";

interface ChangePasswordFormProps {
	onSuccess?: () => void;
}

const ChangePasswordForm: React.FC<ChangePasswordFormProps> = ({ onSuccess }) => {
	const { t } = useTranslation();
	const { methods, onSubmit, changePasswordMutation } = useChangePasswordMutate(onSuccess);
	const [showOldPassword, setShowOldPassword] = useState(false);
	const [showNewPassword, setShowNewPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);

	const {
		handleSubmit,
		control,
		setValue,
		clearErrors,
		formState: { errors },
	} = methods;

	const newPasswordValue = useWatch({ control, name: "newPassword" }) || "";

	const renderVisibilityToggle = (visible: boolean, toggle: () => void) => (
		<IconButton
			onClick={toggle}
			disabled={changePasswordMutation.isPending}
			size="small"
			type="button"
			className="text-gray-400 hover:text-gray-600"
			aria-label={visible ? "Hide password" : "Show password"}>
			{visible ? <VisibilityOffOutlinedIcon fontSize="medium" /> : <VisibilityOutlinedIcon fontSize="medium" />}
		</IconButton>
	);

	return (
		<>
			<p className="mb-4 text-sm text-gray-600">{t("psdaCommon.profile.changePassword.subtitle")}</p>

			{errors.root ? (
				<div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4">
					<p className="text-sm text-red-600">{errors.root.message}</p>
				</div>
			) : null}

			<FormProvider {...methods}>
				<form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
					<Controller
						name="oldPassword"
						control={control}
						render={({ field, fieldState }) => (
							<InputField
								id="change-password-old"
								{...AUTH_INPUT_FIELD_PROPS}
								label={t("psdaCommon.profile.changePassword.oldPasswordLabel")}
								type={showOldPassword ? "text" : "password"}
								placeholder={t("common.form.placeholders.enterPassword")}
								startIcon={<KeyOutlinedIcon fontSize="medium" />}
								error={!!fieldState.error}
								helperText={fieldState.error?.message}
								disabled={changePasswordMutation.isPending}
								endIcon={renderVisibilityToggle(showOldPassword, () => setShowOldPassword(v => !v))}
								{...field}
								onChange={e => {
									field.onChange(e);
									if (errors.root) clearErrors("root");
								}}
							/>
						)}
					/>

					<Controller
						name="newPassword"
						control={control}
						render={({ field, fieldState }) => (
							<InputField
								id="change-password-new"
								{...AUTH_INPUT_FIELD_PROPS}
								label={t("psdaCommon.profile.changePassword.newPasswordLabel")}
								type={showNewPassword ? "text" : "password"}
								placeholder={t("common.form.placeholders.enterPassword")}
								startIcon={<KeyOutlinedIcon fontSize="medium" />}
								error={!!fieldState.error}
								helperText={fieldState.error?.message}
								disabled={changePasswordMutation.isPending}
								endIcon={renderVisibilityToggle(showNewPassword, () => setShowNewPassword(v => !v))}
								{...field}
								onChange={e => {
									field.onChange(e);
									if (errors.root) clearErrors("root");
								}}
							/>
						)}
					/>

					<PasswordStrengthHints
						password={newPasswordValue}
						onSuggestPassword={suggested => {
							setValue("newPassword", suggested, { shouldValidate: true, shouldDirty: true });
							setValue("confirmPassword", suggested, { shouldValidate: true, shouldDirty: true });
							if (errors.root) clearErrors("root");
						}}
					/>

					<Controller
						name="confirmPassword"
						control={control}
						render={({ field, fieldState }) => (
							<InputField
								id="change-password-confirm"
								{...AUTH_INPUT_FIELD_PROPS}
								label={t("psdaCommon.profile.changePassword.confirmPasswordLabel")}
								type={showConfirmPassword ? "text" : "password"}
								placeholder={t("common.form.placeholders.confirmPassword")}
								startIcon={<KeyOutlinedIcon fontSize="medium" />}
								error={!!fieldState.error}
								helperText={fieldState.error?.message}
								disabled={changePasswordMutation.isPending}
								endIcon={renderVisibilityToggle(showConfirmPassword, () => setShowConfirmPassword(v => !v))}
								{...field}
								onChange={e => {
									field.onChange(e);
									if (errors.root) clearErrors("root");
								}}
							/>
						)}
					/>

					<Button
						type="submit"
						size="auth"
						loading={changePasswordMutation.isPending}
						text={t("psdaCommon.profile.changePassword.submit")}
						className={AUTH_SUBMIT_BUTTON_CLASS}
						disabled={changePasswordMutation.isPending}
					/>
				</form>
			</FormProvider>
		</>
	);
};

export default ChangePasswordForm;
